/**
 * What a folder picker shows when its folder list is not a plain list
 * (decision 18; LB2-T3 review R3 / BF-03 / BF-04 / BF-07).
 *
 * A failed read of the one folder list (lib/use-library-folders.ts, or the
 * System Library's useSysLibFolders) is never an empty Library: every picker
 * that lists folders says the list could not be loaded, says what happens
 * instead when that matters (where an upload will land), and offers a retry.
 *
 * A System Library folder that is not published says so next to its name, so
 * a sysadmin never files a document into a draft, deprecated or retired folder
 * without seeing it.
 */
import { Button } from '@/components/ui/button';

export const FOLDER_LIST_ERROR_TEXT = 'The folder list could not be loaded.';

export interface FolderListErrorProps {
  /** What happens instead, in one sentence (e.g. where the upload lands). */
  consequence?: string;
  /** Re-read the list (the query's `refetch`). */
  onRetry?: () => void;
  'data-testid'?: string;
}

export function FolderListError({ consequence, onRetry, 'data-testid': testId }: FolderListErrorProps) {
  return (
    <div
      role="alert"
      className="flex flex-wrap items-center gap-x-2 text-xs text-destructive"
      data-testid={testId ?? 'folder-list-error'}
    >
      <span>
        {FOLDER_LIST_ERROR_TEXT}
        {consequence ? ` ${consequence}` : ''}
      </span>
      {onRetry ? (
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 text-xs"
          onClick={() => onRetry()}
          title="Load the folder list again"
        >
          Retry
        </Button>
      ) : null}
    </div>
  );
}

/** A System Library folder's lifecycle status, shown only when it is not published. */
export function FolderLifecycleNote({ status }: { status?: string | null }) {
  if (!status || status === 'published') return null;
  return (
    <span className="text-muted-foreground" data-testid="folder-lifecycle-note">
      {` · ${status}`}
    </span>
  );
}
